// Shared state, watchers and computed properties for the dashboard layout editor.
import DashboardBlockHelper from "../helpers/DashboardBlockHelper";

/**
 * Create one explicitly positioned dashboard background cell. The cells must
 * not use CSS Grid auto-placement: their count changes while widgets are
 * dragged, which can otherwise make Grid create an implicit extra row.
 *
 * @param {*} index
 * @param {*} columnCount
 */
function createDashboardGridCell(index, columnCount) {
    return {
        key: `dashboard-grid-cell-${index + 1}`,
        column: (index % columnCount) + 1,
        row: Math.floor(index / columnCount) + 1,
    };
}

export const dashboardLayoutEditorData = () => {
    return {
        loading: true,
        dataProvider: null,
        grid: null,
        breakpoints: [],
        blocks: [],
        visibility: null,
        activeBreakpointId: null,
        activeBlock: null,
        showGuides: true,
        showHiddenElements: false,
        featureModalOpen: false,
        featureMessage: null,
        blueprintDataStartState: null,
        refreshKey: 0,
        widgetTypes: null,
        dashboardCanvasDragReferenceId: null,
        dashboardCanvasDropTarget: null,
        dashboardCanvasCommittedReferenceId: null,
        dashboardCanvasResizeState: null,
        dashboardCanvasContextMenuReferenceId: null,
        dashboardTabs: [],
        activeDashboardTabId: null,
    };
};

export const dashboardLayoutEditorWatch = {
    /**
     * Active block.
     */
    activeBlock() {
        if (!this.activeBlock) {
            this.dashboardCanvasDropTarget = null;
        }
    },
};

export const dashboardLayoutEditorComputed = {
    /**
     * Dashboard grid columns.
     */
    dashboardGridColumns() {
        return Number(this.grid?.numberOfColumns || 12);
    },
    /**
     * Dashboard grid rows.
     */
    dashboardGridRows() {
        return 8;
    },
    /**
     * Dashboard grid cells.
     */
    dashboardGridCells() {
        const cells = [];
        // Always render the full background grid. Widget and drop-target
        // positions are layered on these explicit cells and never alter the
        // number of grid items.
        const cellCount = this.dashboardGridColumns * this.dashboardGridRows;

        for (let index = 0; index < cellCount; index += 1) {
            cells.push(createDashboardGridCell(index, this.dashboardGridColumns));
        }

        return cells;
    },
    /**
     * Dashboard grid style.
     */
    dashboardGridStyle() {
        return {
            gridTemplateColumns: `repeat(${this.dashboardGridColumns}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${this.dashboardGridRows}, minmax(0, 1fr))`,
        };
    },
    /**
     * Dashboard canvas grid key.
     */
    dashboardCanvasGridKey() {
        return `${this.activeBreakpointId || 'breakpoint'}-${this.getActiveDashboardTabId()}`;
    },
    /**
     * Active dashboard widgets.
     */
    activeDashboardWidgets() {
        if (!this.activeBreakpoint) {
            return [];
        }

        const widgets = this.getDashboardWidgetsForBreakpoint(this.activeBreakpoint.id);
        const activeWidgets = [];
        const activeTabId = this.getActiveDashboardTabId();

        widgets.forEach((block) => {
            const tabId = block.tabId || this.getDefaultDashboardTabId();

            if (tabId === activeTabId) {
                if (this.isChecksAccordionChildInActiveTab(block.referenceId)) {
                    return;
                }

                activeWidgets.push(block);
            }
        });

        return activeWidgets;
    },
    /**
     * Hidden dashboard widgets.
     */
    hiddenDashboardWidgets() {
        if (!this.activeBreakpoint) {
            return [];
        }

        const hiddenWidgets = [];
        const activeTabId = this.getActiveDashboardTabId();

        this.activeBreakpoint.widgetPositioning.forEach((positioning) => {
            if (!positioning.hidden) {
                return;
            }

            const block = DashboardBlockHelper.getBlock(this.blocks, positioning.referenceId);

            if (!block) {
                return;
            }

            const tabId = block.tabId || this.getDefaultDashboardTabId();

            if (tabId !== activeTabId) {
                return;
            }

            if (this.isChecksAccordionChildInActiveTab(block.referenceId)) {
                return;
            }

            hiddenWidgets.push({
                ...block,
                orderIndex: Number(positioning.orderIndex ?? 0),
            });
        });

        hiddenWidgets.sort((currentBlock, nextBlock) => currentBlock.orderIndex - nextBlock.orderIndex);

        return hiddenWidgets;
    },
    /**
     * Active dashboard blocks.
     */
    activeDashboardBlocks() {
        return this.activeDashboardWidgets;
    },
    /**
     * Active breakpoint.
     */
    activeBreakpoint() {
        for (let index = 0; index < this.breakpoints.length; index += 1) {
            const breakpoint = this.breakpoints[index];

            if (breakpoint.id === this.activeBreakpointId) {
                return breakpoint;
            }
        }

        return null;
    },
    /**
     * Blueprint data.
     */
    blueprintData() {
        const blueprintData = {
            title: this.dataProvider.title,
            tabs: this.dashboardTabs,
            breakpoints: this.breakpoints,
            blocks: this.blocks,
        };

        if (this.dataProvider.metadataHandler.getMetadata()) {
            blueprintData.metadata = this.dataProvider.metadataHandler.getMetadata();
        }

        if (this.dataProvider.headerHandler.getHeaders()) {
            blueprintData.headers = this.dataProvider.headerHandler.getHeaders();
        }

        return blueprintData;
    },
    /**
     * Vertical id.
     */
    verticalId() {
        return this.queryParams.verticalId;
    },
    /**
     * Version id.
     */
    versionId() {
        return this.queryParams.versionId;
    },
    /**
     * Active dashboard tab.
     */
    activeDashboardTab() {
        const activeTabId = this.getActiveDashboardTabId();

        for (let index = 0; index < this.dashboardTabs.length; index += 1) {
            const tab = this.dashboardTabs[index];

            if (tab.id === activeTabId) {
                return tab;
            }
        }

        if (this.dashboardTabs.length) {
            return this.dashboardTabs[0];
        }

        return null;
    },
};
